import Line from "../../components/InputComponents/Line";
import ParcelsMenu from "./ParcelsMenu";
import { useContext, useEffect, useState } from "react";
import axiosClient from "../../api/api";
import UserPagination from "../../components/UserPagination/UserPagination";
import { BiSearch } from "react-icons/bi";
import WarehouseCard from "./WarehouseCard";
import SentCard from "./SentCard";
import ArrivedCard from "./ArrivedCard";
import TakeoutCard from "./TakeoutCard";
import { PrimaryContext } from "../../components/MainContext/MainContext";
import { useTranslation } from "react-i18next";
import Button from "../../components/InputComponents/Button";
import { errorToast, successToast } from "../../adminPanel/components/toastAlerts/ToastAlerts";
import DeliveryFeeCard from "./DeliveryFeeCard";
import { paletteFor } from "./deliveryPalette";

const MyParcels = () => {
  const { t } = useTranslation();
  //parcels
  const [parcels, setParcels] = useState([]);
  //updater
  const [updatePacks, setUpdatePacks] = useState(null);
  //loader
  const [isLoading, setIsLoading] = useState(true);
  //total amount
  const [totalAmount, setTotalAmount] = useState(0);
  //currentPage
  const [currentPage, setCurrentPage] = useState(0);
  //type
  const [type, setType] = useState(1);
  //search
  const [searchValue, setSearchValue] = useState("");
  //data from context
  const { updateUser, setUpdateUser } = useContext(PrimaryContext);
  //dummy text for loader
  const dummyArray = Array.from({ length: 8 });
  // Courier deliveries: one fee per address, covering several parcels. Only
  // loaded on the arrived tab — that is the only place ready parcels appear,
  // and the endpoint re-groups on every read.
  const [deliveries, setDeliveries] = useState([]);
  const [payingDelivery, setPayingDelivery] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    axiosClient
      .get(
        `/packs?page=${
          currentPage + 1
        }&per_page=${12}&status=${type}&tracking_number=${searchValue}`
      )
      .then(({ data }) => {
        setParcels(data.data);
        setTotalAmount(data.meta.total);
        setIsLoading(false);
      });
  }, [currentPage, type, searchValue, updatePacks]);

  useEffect(() => {
    setCurrentPage(0);
  }, [type]);

  useEffect(() => {
    if (type !== 3) return;
    axiosClient
      .get("/delivery-charges")
      .then(({ data }) => setDeliveries(data.charges || []))
      .catch(() => setDeliveries([]));
  }, [type, updatePacks]);

  // A fee of zero means the group is under the free weight — nothing to show,
  // and the parcels stay plain, exactly as they were before.
  // The delivery being put together now comes first, newest first, with its
  // parcels right under it; one the courier already made (its parcels are on
  // the delivered tab) goes after the list, so a balance left on it reads as
  // the past trip it is rather than a second fee for the parcels shown.
  const chargeable = deliveries
    .filter((d) => Number(d.amount) > 0)
    .sort((a, b) => (a.frozen === b.frozen ? b.id - a.id : a.frozen ? 1 : -1));
  const currentDeliveries = chargeable.filter((d) => !d.frozen);
  const pastDeliveries = chargeable.filter((d) => d.frozen);

  // pack id -> the colour of the delivery it belongs to
  const deliveryByPack = {};
  chargeable.forEach((d, i) => {
    (d.pack_ids || []).forEach((id) => {
      deliveryByPack[id] = paletteFor(i);
    });
  });

  // `endpoint` picks between paying just the courier fee and settling the whole
  // delivery — same guard rails, same refresh, so they cannot drift apart.
  const payDelivery = (id, method, endpoint = "pay") => {
    setPayingDelivery(id);
    axiosClient
      .post(`/delivery-charges/${id}/${endpoint}`, { method })
      .then((res) => {
        successToast(t("payment_success"));
        setUpdatePacks(res);
        updateUser(res);
      })
      .catch((err) => {
        errorToast(
          typeof err?.response?.data === "string"
            ? err.response.data
            : err?.response?.data?.error || t("something_went_wrong")
        );
      })
      .finally(() => setPayingDelivery(null));
  };

  // The server says why it refused (paid at the courier, short balance...) —
  // it used to be swallowed, so a click that did nothing looked like nothing.
  const payRefused = (err) =>
    errorToast(
      err?.response?.data?.error ||
        (err?.response?.status === 402 ? t("not_enough_funds") : t("error_msg"))
    );

  //handle pay
  const payPack = (id) => {
    axiosClient
      .post(`/pakcPay/${id}`)
      .then((res) => {
        successToast(t("payment_success"));
        setUpdatePacks(res);
        updateUser(res);
      })
      .catch(payRefused);
  };

  //handle pay with bonus points
  const payPackBonusPoints = (id) => {
    axiosClient
      .post(`/pakcPay/${id}?method=points`)
      .then((res) => {
        successToast(t("payment_success"));
        setUpdatePacks(res);
        updateUser(res);
      })
      .catch(payRefused);
  };

  //pay all
  const handlePayAll = (packageType) => {
    axiosClient
      .get(`/payAll?status=${packageType}`)
      .then((res) => {
        // Success only when something was taken: parcels the courier collects
        // for are skipped, and "paid" over 0 GEL read as if it had worked.
        if (Number(res?.data?.paid_now) > 0) successToast(t("payment_success"));
        else errorToast(t("nothing_to_pay_here"));
        setUpdatePacks(res);
        setUpdateUser(res);
      })
      .catch(payRefused);
  };

  return (
    <div>
      <div className="flex flex-col gap-2 sm:w-full sm:justify-between md:items-center">
        <Line className="bg-teal_600 h-1 w-10" />
        <h2 className="text-black_900 text-2xl md:text-xl font-semibold">
          {t("my_parcels")}
        </h2>
      </div>
      <ParcelsMenu setter={setType} />
      <div className="flex gap-2 mt-5 sm:flex-col">
        <div className="w-full flex items-center gap-2">
          <div
            className="flex items-center gap-4 w-full bg-white shadow-bs1
              font-normal h-12 md:max-w-full px-2 py-2 rounded-[12px] text-gray_600 text-left"
          >
            <input
              placeholder={t("tracking_number")}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              value={searchValue}
              className="bg-transparent w-full"
            />
            <div className="w-10 h-10 bg-teal_600_b2 rounded-lg p-1 border-2 border-teal_600">
              <BiSearch className="w-full h-full text-white" />
            </div>
          </div>
        </div>
        {(type === 2 || type === 3) && (
          <Button
            className="cursor-pointer flex items-center justify-center min-w-[310px] w-auto sm:w-full sm:min-w-full"
            shape="RoundedBorder14"
            size="lg"
            variant="OutlineTeal600"
            onClick={() => {
              handlePayAll(type);
            }}
          >
            <div className="font-normal text-md text-center text-white_A700">
              {t("pay_all")}
            </div>
          </Button>
        )}
      </div>
      <UserPagination
        dataLength={totalAmount}
        itemsPerPage={12}
        pageNumberSetter={setCurrentPage}
      >
        <div
          className={`afMd:shadow-bs1 afMd:rounded-[30px] my-5
  afMd:bg-white ${
    parcels?.length !== 0 && "md:grid md:grid-cols-2 md:gap-5 sm:grid-cols-1 "
  }`}
        >
          {type === 3 &&
            !isLoading &&
            currentDeliveries.map((d, i) => (
              <DeliveryFeeCard
                key={`delivery-${d.id}`}
                charge={d}
                palette={paletteFor(i)}
                onPay={payDelivery}
                onPayAll={(chargeId, method) =>
                  payDelivery(chargeId, method, "payAll")
                }
                paying={payingDelivery === d.id}
              />
            ))}
          {isLoading ? (
            dummyArray.map((_, index) => {
              return (
                <div
                  className={`animate-pulse bg-gray-200 w-full h-16 md:h-32 md:rounded-lg border-t border-white ${
                    index === 0 && "afMd:border-t-0 afMd:rounded-t-3xl"
                  } ${dummyArray.length - 1 === index && "afMd:rounded-b-3xl"}`}
                  key={index}
                ></div>
              );
            })
          ) : parcels?.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center">
              <h2 className="font-bold">{t("empty")}</h2>
            </div>
          ) : (
            parcels.map((item, index) => {
              return (
                <div
                  key={item.id}
                  className={`${
                    index !== 0 &&
                    parcels.length !== 0 &&
                    "border-t border-gray-200 md:border-t-0"
                  }`}
                >
                  {" "}
                  {type === 1 && (
                    <WarehouseCard
                      data={item}
                      payPack={payPack}
                      setUpdatePacks={setUpdatePacks}
                      payPackBonusPoints={payPackBonusPoints}
                    />
                  )}
                  {type === 2 && (
                    <SentCard
                      data={item}
                      payPack={payPack}
                      setUpdatePacks={setUpdatePacks}
                      payPackBonusPoints={payPackBonusPoints}
                    />
                  )}
                  {type === 3 && (
                    <ArrivedCard
                      data={item}
                      payPack={payPack}
                      setUpdatePacks={setUpdatePacks}
                      payPackBonusPoints={payPackBonusPoints}
                      deliveryPalette={deliveryByPack[item.id]}
                    />
                  )}
                  {type === 4 && (
                    <TakeoutCard
                      data={item}
                      className={`${
                        index !== 0 &&
                        parcels.length !== 0 &&
                        "border-t border-gray-200 md:border-t-0"
                      }`}
                    />
                  )}
                </div>
              );
            })
          )}
          {type === 3 &&
            !isLoading &&
            pastDeliveries.map((d, i) => (
              <DeliveryFeeCard
                key={`delivery-${d.id}`}
                charge={d}
                palette={paletteFor(currentDeliveries.length + i)}
                onPay={payDelivery}
                onPayAll={(chargeId, method) =>
                  payDelivery(chargeId, method, "payAll")
                }
                paying={payingDelivery === d.id}
              />
            ))}
        </div>
      </UserPagination>
    </div>
  );
};

export default MyParcels;
